import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import Board from '../common/board';
import AddCardLink from './add_card_link';
import NewMenuForm from './new_menu_form';
import NewMenuButton from './new_menu_button';
import MenuHeader from './menu_header';
import {
  boardWithMealMoved,
  boardWithMealRemoved,
  boardWithMenuAdded,
  boardWithMenuDeleted,
  boardWithMenuDragged,
  boardWithMenuRenamed,
  isBoardPublishedToSpace,
  scrollNewCardIntoView,
} from '../../services/smorg_board';
import { useIsMobile } from '../common/layout_hooks';
import usePrevious from '../../services/use_previous';
import NewRecipesEntryForm from './new_recipes_entry_form';
import MobileBoardLaneNavigator from '../common/mobile_board_lane_navigator';
import MealBasketWidget from '../../features/end_user_shopping/components/meal_basket_widget';
import { ActivityObjectType, ContainerType } from '../../API';
import RecipesBoardCard from './recipes_board_card';
// import { recipeCollectionsCardDOMID } from '../../services/meals';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useGetDistilledMealsByParentQuery,
  useGetDistilledSharedMealsByParentQuery,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
  useLazyGetFullMealQuery,
  useUpdateMealMutation,
  useUpdateSmorgBoardMutation,
  useUpdateSmorgBoardOptimisticallyMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { userIsCreator } from '../../services/users';
import { mealsRemovedFromMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import {
  copyMealToBoard,
  newMealAddedToRecipesBoard,
} from '../../operations/recipes_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { useTracker } from '../../operations/tracking_operations';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { recipeCollectionsCardDOMID } from '../../services/meals';
import ResourceLoadingModal from '../common/resource_loading_modal';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const labels = {
  'Add another lane': '+ Add another menu',
  'Delete lane': 'Delete menu',
  'Lane actions': 'Menu actions',
  button: {
    'Add lane': 'Add menu',
    'Add card': 'Add meal',
    Cancel: 'Cancel',
  },
  placeholder: {
    title: 'title',
    description: 'description',
    label: 'label',
  },
};

const customTranslation = (key) => labels[key];

const components = {
  AddCardLink,
  NewCardForm: NewRecipesEntryForm,
  Card: RecipesBoardCard,
  NewLaneForm: NewMenuForm,
  NewLaneSection: NewMenuButton,
  LaneHeader: MenuHeader,
};

/**
 * Recipes board view for desktops, using Trello-like lanes.
 * Users who see this:
 *  - Smorg Studio users aka health pros
 *  - End users of the community website
 *  - End users on desktop webapp
 */
const RecipesBoardTrelloView = ({
  recipesBoardId,
  boardCss,
  heightRuleCss,
  freeHeight,
  onClickMeal,
  blankBoardHelpContent,
}) => {
  const dispatch = useDispatch();

  const { data: userProfile, isLoading: isUserProfileLoading } =
    useGetUserProfileQuery();

  const userIsEndUser = !userIsCreator(userProfile);

  const theme = useTheme();

  const { data: recipesBoard, isLoading: isOwnRecipesBoardLoading } =
    useGetSmorgBoardQuery(recipesBoardId, { skip: !recipesBoardId });

  const { data: distilledMeals, isLoading: isDistilledMealsLoading } =
    useGetDistilledMealsByParentQuery(recipesBoardId, {
      skip: !recipesBoardId,
    });

  const {
    data: distilledSharedMeals,
    isLoading: isDistilledSharedMealsLoading,
  } = useGetDistilledSharedMealsByParentQuery(recipesBoardId, {
    skip: !recipesBoardId,
  });

  const prevRecipesBoardId = usePrevious(recipesBoardId);

  const [deleteMeal] = useDeleteMealMutation();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [updateSmorgBoardOptimistically] =
    useUpdateSmorgBoardOptimisticallyMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  useEffect(() => {
    if (recipesBoardId !== prevRecipesBoardId) {
      // After switching to a new board, make sure the first lane is
      // visible at the top left
      window.setTimeout(() => {
        const selector = `.smooth-dnd-draggable-wrapper:first-child`;
        const firstLaneEl = document.querySelector(selector);
        if (firstLaneEl) {
          console.log('Scrolling firstLaneEl into view');
          firstLaneEl.scrollIntoView();
        }
      }, 100);
    }
  }, [prevRecipesBoardId, recipesBoardId]);

  const prevDistilledMeals = usePrevious(distilledMeals);

  useEffect(() => {
    // If a new meal was just added, scroll the lane
    // to make the new meal visible.
    scrollNewCardIntoView(distilledMeals, prevDistilledMeals);
  }, [distilledMeals, prevDistilledMeals]);

  const isSharedProgrammeBoard = [
    ContainerType.PROGRAMME,
    ContainerType.SHARED_PROGRAMME,
  ].includes(recipesBoard?.embeddedInContainerType);

  const isSmorgStudio = userIsCreator(userProfile);

  const tracker = useTracker();

  const { data: healthProSpace, isLoading: isHealthProSpaceLoading } =
    useGetSpaceQuery();

  const { data: currentSpaceMembership, isLoading: spaceMembershipLoading } =
    useGetCurrentSpaceMembershipQuery(undefined, {
      skip: !userProfile || isSmorgStudio,
    });

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const isPublishedToSpace = isBoardPublishedToSpace(
    recipesBoard,
    healthProSpace,
  );

  const isProductizedSharedBoard = isPublishedToSpace;

  const isReadOnly =
    userIsEndUser && (isSharedProgrammeBoard || isProductizedSharedBoard);

  const recipesAIGenerationJobs = useSelector(
    (state) => state.aiGeneration.aiGenerationJobs,
  );

  const memoizedReactTrelloData = useMemo(() => {
    const reactTrelloCardDataForMeal = (mealID, menuID) => {
      const distilledMeal =
        (distilledMeals || {})[mealID] || (distilledSharedMeals || {})[mealID];
      if (!distilledMeal) {
        // Meal not yet loaded
        return null;
      }
      return {
        id: distilledMeal.id,
        title: distilledMeal.recipes[0].title,
        description: distilledMeal.recipes[0].shortDescription || '',
        style: { _readOnly: isReadOnly, _menuID: menuID },
      };
    };

    const reactTrelloCardDataForAIJob = (aiJob) => {
      return {
        id: aiJob.recipeJobID,
        title: aiJob.aiPrompt,
        style: { _recipeJobID: aiJob.recipeJobID },
      };
    };

    const reactTrelloCardsForBoardMenu = (menu) => {
      const aiJobCardDataAtTop = recipesAIGenerationJobs
        .filter(
          (aiJob) =>
            aiJob.recipesBoardID === recipesBoardId &&
            aiJob.menuID === menu.id &&
            aiJob.addToTop,
        )
        .map(reactTrelloCardDataForAIJob);
      const mealCardData = (menu?.mealIDs || []).map((mealID) =>
        reactTrelloCardDataForMeal(mealID, menu.id),
      );
      const aiJobCardDataAtBottom = recipesAIGenerationJobs
        .filter(
          (aiJob) =>
            aiJob.recipesBoardID === recipesBoardId &&
            aiJob.menuID === menu.id &&
            !aiJob.addToTop,
        )
        .map(reactTrelloCardDataForAIJob);
      return [...aiJobCardDataAtTop, ...mealCardData, ...aiJobCardDataAtBottom];
    };

    const reactTrelloDataForBoardMenu = (menu) => ({
      id: menu.id,
      title: menu.title,
      titleStyle: isReadOnly && {
        _isBoardReadOnly: isReadOnly,
      },
      cards: reactTrelloCardsForBoardMenu(menu).filter(
        (cardData) => !!cardData,
      ),
    });

    return {
      lanes: (recipesBoard?.menus || []).map(reactTrelloDataForBoardMenu),
    };
  }, [
    distilledMeals,
    distilledSharedMeals,
    isReadOnly,
    recipesAIGenerationJobs,
    recipesBoard?.menus,
    recipesBoardId,
  ]);

  const afterCardAdd = useCallback(
    async (reactTrelloCard, laneId) => {
      console.log({ reactTrelloCard });
      const addToTop = reactTrelloCard.style?._add_to_top;
      const newMeal = reactTrelloCard.style._meal;
      const searchResultMealID = reactTrelloCard.style._searchResult?.mealID;
      const shouldOpenMealDetail =
        !!reactTrelloCard.style?._should_open_meal_detail;
      if (newMeal) {
        await newMealAddedToRecipesBoard(
          recipesBoard,
          laneId,
          newMeal,
          addToTop ? 0 : null,
          customerFoodDataID,
          tracker,
          userProfile,
          currentSpaceMembership,
          healthProSpace,
          createMeal,
          updateSmorgBoard,
          updateMeal,
          lazyGetMeal,
          (insertedMeal) => {
            if (shouldOpenMealDetail) {
              onClickMeal(insertedMeal.id);
            }
          },
        );
        if (isSmorgStudio) {
          reportAccountActivitySignalOperation({
            spaceID: healthProSpace.id,
            friendlyUsername,
            lastActivityObjectType: ActivityObjectType.RECIPES,
            lastActivityObjectID: recipesBoardId,
          });
        }
      } else if (searchResultMealID) {
        const { data: fullMeal } = await lazyGetMeal(searchResultMealID);
        const cb = (copiedMeal) => {
          window.setTimeout(() => {
            const el = document.getElementById(
              recipeCollectionsCardDOMID(copiedMeal.id),
            );
            if (el) {
              el.scrollIntoView({ behavior: 'smooth' });
            }
          }, 200);
        };

        await copyMealToBoard(
          fullMeal,
          recipesBoard,
          laneId,
          currentSpaceMembership,
          updateSmorgBoard,
          createMeal,
          userProfile,
          tracker,
          healthProSpace,
          cb,
          dispatch,
          addToTop ? 0 : null,
        );
      }
      if (isSmorgStudio) {
        reportAccountActivitySignalOperation({
          spaceID: healthProSpace.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.RECIPES,
          lastActivityObjectID: recipesBoardId,
        });
      }
    },
    [
      createMeal,
      currentSpaceMembership,
      customerFoodDataID,
      dispatch,
      friendlyUsername,
      healthProSpace,
      isSmorgStudio,
      lazyGetMeal,
      onClickMeal,
      recipesBoard,
      recipesBoardId,
      tracker,
      updateMeal,
      updateSmorgBoard,
      userProfile,
    ],
  );

  const onCardClick = useCallback(
    (cardId, metadata, laneId) => {
      console.log({ cardId, metadata, laneId });
      const distilledMeal =
        distilledMeals[cardId] || distilledSharedMeals[cardId];
      console.log({ distilledMeal });
      if (!distilledMeal) {
        return;
      }
      onClickMeal(cardId);
    },
    [distilledMeals, onClickMeal, distilledSharedMeals],
  );

  const afterCardDrag = useCallback(
    async (
      cardId,
      sourceLaneId,
      targetLaneId,
      position,
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      cardDetails,
    ) => {
      const newBoard = boardWithMealMoved(
        recipesBoard,
        cardId,
        sourceLaneId,
        targetLaneId,
        position,
      );
      await updateSmorgBoardOptimistically(newBoard);
      if (isSmorgStudio) {
        reportAccountActivitySignalOperation({
          spaceID: healthProSpace?.id,
          friendlyUsername,
          lastActivityObjectType: ActivityObjectType.RECIPES,
          lastActivityObjectID: recipesBoardId,
        });
      }

      // Cancel the drop, because the board will re-render with the new structure.
      return false;
    },
    [
      friendlyUsername,
      healthProSpace?.id,
      isSmorgStudio,
      recipesBoard,
      recipesBoardId,
      updateSmorgBoardOptimistically,
    ],
  );

  const afterLaneUpdate = useCallback(
    async (laneId, data) => {
      console.log({ laneId, data });
      // Only the title can be edited
      const updatedTitle = data.title;
      if (updatedTitle && updatedTitle.length > 0) {
        const newBoard = boardWithMenuRenamed(
          recipesBoard,
          laneId,
          updatedTitle,
        );
        await updateSmorgBoard(newBoard);
      }
    },
    [recipesBoard, updateSmorgBoard],
  );

  const afterLaneAdd = useCallback(
    async (params) => {
      const { id, title } = params;
      if (title && title.length > 0) {
        const newBoard = boardWithMenuAdded(recipesBoard, id, title);
        await updateSmorgBoard(newBoard);
        if (isSmorgStudio) {
          reportAccountActivitySignalOperation({
            spaceID: healthProSpace?.id,
            friendlyUsername,
            lastActivityObjectType: ActivityObjectType.RECIPES,
            lastActivityObjectID: recipesBoardId,
          });
        }
      }
    },
    [
      friendlyUsername,
      healthProSpace?.id,
      isSmorgStudio,
      recipesBoard,
      recipesBoardId,
      updateSmorgBoard,
    ],
  );

  const afterLaneDelete = useCallback(
    async (laneId) => {
      const mealIds = recipesBoard.menus.find((m) => m.id === laneId).mealIDs;
      dispatch(mealsRemovedFromMealBasket(mealIds));
      try {
        await Promise.all(
          mealIds.map((mealID) =>
            deleteMeal({ id: mealID, parentID: recipesBoardId }),
          ),
        );
      } catch (e) {
        console.warn(e);
      }
      const newBoard = boardWithMenuDeleted(recipesBoard, laneId);
      return updateSmorgBoard(newBoard);
    },
    [deleteMeal, dispatch, recipesBoard, recipesBoardId, updateSmorgBoard],
  );

  const afterLaneDrag = useCallback(
    async (removedIndex, addedIndex) => {
      const newBoard = boardWithMenuDragged(
        recipesBoard,
        removedIndex,
        addedIndex,
      );
      return updateSmorgBoard(newBoard);
    },
    [recipesBoard, updateSmorgBoard],
  );

  const afterCardDelete = useCallback(
    async (cardId, laneId) => {
      dispatch(mealsRemovedFromMealBasket([cardId]));
      const updatedParentBoard = boardWithMealRemoved(
        recipesBoard,
        cardId,
        laneId,
      );
      return Promise.all([
        deleteMeal({
          id: cardId,
          parentID: recipesBoardId,
        }),
        updateSmorgBoard(updatedParentBoard),
      ]);
    },
    [deleteMeal, dispatch, recipesBoard, recipesBoardId, updateSmorgBoard],
  );

  const isBlank =
    Object.values(distilledMeals || {}).length +
      Object.values(distilledSharedMeals || {}).length ===
    0;

  const isMobile = useIsMobile();

  const boardScrollableRef = useRef();

  document.documentElement.style.setProperty(
    '--recipes-free-height',
    `${freeHeight}px`,
  );

  const isLoading =
    !recipesBoardId ||
    isUserProfileLoading ||
    isOwnRecipesBoardLoading ||
    isDistilledMealsLoading ||
    isDistilledSharedMealsLoading ||
    isHealthProSpaceLoading ||
    spaceMembershipLoading;

  if (isLoading) {
    return <ResourceLoadingModal />;
  }

  return (
    <div
      className={classNames('smorg-board-container', {
        userIsEndUser /* , mealDetailVisible */,
      })}
      id="smorg-board">
      <MealBasketWidget />
      <Board
        appBoardId={recipesBoardId}
        editable={!isReadOnly}
        draggable={!isReadOnly}
        cardDraggable={!isReadOnly}
        editLaneTitle={!isReadOnly}
        canAddLanes={!isReadOnly}
        data={memoizedReactTrelloData}
        components={components}
        onCardAdd={afterCardAdd}
        onCardClick={onCardClick}
        onLaneAdd={afterLaneAdd}
        onLaneDelete={afterLaneDelete}
        onLaneUpdate={afterLaneUpdate}
        onCardDelete={afterCardDelete}
        handleDragEnd={afterCardDrag}
        handleLaneDragEnd={afterLaneDrag}
        boardScrollableRef={boardScrollableRef}
        backdropContent={
          <>
            {isMobile && (
              <MobileBoardLaneNavigator
                boardScrollableRef={boardScrollableRef}
                laneIDs={memoizedReactTrelloData.lanes.map((l) => l.id)}
                positionStyle={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                }}
                renderItem={(itemIndexZeroBased, isSelected, onClick) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    style={{
                      backgroundColor: isSelected
                        ? theme.palette.primary.light
                        : theme.palette.grey[300],
                      borderRadius: '50%',
                      width: 10,
                      height: 10,
                      margin: '8px',
                      cursor: 'pointer',
                    }}
                    onClick={onClick}>
                    &nbsp;
                  </div>
                )}
              />
            )}
            {isBlank && blankBoardHelpContent}
          </>
        }
        style={boardCss}
        backdropStyle={heightRuleCss}
        t={customTranslation}
      />
    </div>
  );
};

RecipesBoardTrelloView.propTypes = {
  recipesBoardId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  boardCss: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  heightRuleCss: PropTypes.object.isRequired,
  freeHeight: PropTypes.number.isRequired,
  onClickMeal: PropTypes.func.isRequired,
  blankBoardHelpContent: PropTypes.element,
};

RecipesBoardTrelloView.defaultProps = {
  blankBoardHelpContent: null,
};

export default RecipesBoardTrelloView;
